import { useEffect, useMemo, useState } from "react";

import {
  RefreshCw,
  Search,
  Plus,
  Settings2,
  Loader2,
  Store,
  Layers3,
} from "lucide-react";

const DeliveryChargesTab = ({
  shops = [],
  deliveryCharges = [],
  loading = false,
  fetchDeliveryCharges,
  onAdd,
  onManage,
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchDeliveryCharges?.("");
  }, []);

  // =========================================================
  // HELPERS
  // =========================================================

  const getShop = (restaurantId) => {
    if (!restaurantId) return null;

    return (shops || []).find(
      (shop) =>
        String(shop?.shop_code) ===
        String(restaurantId),
    );
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);

    if (Number.isInteger(number)) {
      return number;
    }

    return number.toFixed(2);
  };

  const formatRange = (
    minimum,
    maximum,
    prefix = "",
    suffix = "",
  ) => {
    const min = Number(minimum || 0);
    const max = Number(maximum || 0);

    if (max <= 0) {
      return `${prefix}${formatNumber(min)}+${suffix}`;
    }

    return `${prefix}${formatNumber(min)} - ${prefix}${formatNumber(
      max,
    )}${suffix}`;
  };

  // =========================================================
  // FILTER RESTAURANT GROUPS
  // =========================================================

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return deliveryCharges || [];
    }

    return (deliveryCharges || []).filter((group) => {
      const restaurantId = group?.restaurant_id;

      const shop = getShop(restaurantId);

      return (
        String(restaurantId || "global")
          .toLowerCase()
          .includes(query) ||
        String(shop?.shop_name || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [deliveryCharges, shops, search]);

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Delivery Charges
          </h2>

          <p className="mt-0.5 text-xs text-gray-500">
            Manage shop-wise delivery charge slabs.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              fetchDeliveryCharges?.("")
            }
            disabled={loading}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw
              size={14}
              className={
                loading ? "animate-spin" : ""
              }
            />

            Refresh
          </button>

          <button
            type="button"
            onClick={() => onAdd?.()}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-orange-500 px-3 text-xs font-medium text-white transition hover:bg-orange-600"
          >
            <Plus size={14} />
            Add Charges
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />

        <input
          type="text"
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
          placeholder="Search shop or code..."
          className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-xs outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-[#2EC8F1] text-white">
              <tr>
                <th className="w-12 px-3 py-2.5 text-center text-[11px] font-semibold uppercase">
                  #
                </th>

                <th className="w-[230px] px-3 py-2.5 text-left text-[11px] font-semibold uppercase">
                  Shop
                </th>

                <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase">
                  Delivery Slabs
                </th>

                <th className="w-[90px] px-3 py-2.5 text-center text-[11px] font-semibold uppercase">
                  Slabs
                </th>

                <th className="w-[110px] px-3 py-2.5 text-center text-[11px] font-semibold uppercase">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="py-10 text-center"
                  >
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-orange-500" />

                    <p className="mt-2 text-xs text-gray-500">
                      Loading delivery charges...
                    </p>
                  </td>
                </tr>
              ) : filteredGroups.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="py-10 text-center text-xs text-gray-500"
                  >
                    No delivery charges found.
                  </td>
                </tr>
              ) : (
                filteredGroups.map(
                  (group, index) => {
                    const restaurantId =
                      group?.restaurant_id;

                    const shop =
                      getShop(restaurantId);

                    const isGlobal =
                      restaurantId === null ||
                      restaurantId === undefined ||
                      restaurantId === "";

                    const slabs = [
                      ...(group?.deliveryDetails ||
                        []),
                    ].sort((a, b) => {
                      return (
                        Number(
                          a?.minimum_amount || 0,
                        ) -
                        Number(
                          b?.minimum_amount || 0,
                        )
                      );
                    });

                    return (
                      <tr
                        key={
                          isGlobal
                            ? "global-delivery"
                            : restaurantId
                        }
                        className="hover:bg-orange-50/30"
                      >
                        {/* Serial */}
                        <td className="px-3 py-3 text-center text-xs text-gray-500">
                          {index + 1}
                        </td>

                        {/* Shop */}
                        <td className="px-3 py-3">
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-50">
                              <Store
                                size={13}
                                className="text-orange-600"
                              />
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold text-gray-900">
                                {isGlobal
                                  ? "Default / Global"
                                  : shop?.shop_name ||
                                    "Unknown Shop"}
                              </p>

                              <p className="mt-0.5 text-[10px] text-gray-500">
                                {isGlobal
                                  ? "Default delivery rules"
                                  : restaurantId}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* ALL SLABS IN ONE ROW */}
                        <td className="max-w-[700px] px-3 py-2">
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {slabs.length === 0 ? (
                              <span className="text-xs text-gray-400">
                                No slabs
                              </span>
                            ) : (
                              slabs.map((slab) => (
                                <div
                                  key={
                                    slab.pk_charges_id
                                  }
                                  className="min-w-[190px] shrink-0 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2"
                                >
                                  {/* Amount + Distance */}
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-[11px] font-semibold text-gray-800">
                                      {formatRange(
                                        slab.minimum_amount,
                                        slab.maximum_amount,
                                        "₹",
                                      )}
                                    </span>

                                    <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-medium text-gray-500">
                                      {formatRange(
                                        slab.minimum_distance,
                                        slab.maximum_distance,
                                        "",
                                        " KM",
                                      )}
                                    </span>
                                  </div>

                                  {/* Charges */}
                                  <div className="mt-1 flex items-center gap-2 text-[10px]">
                                    <span className="font-medium text-green-600">
                                      Delivery ₹
                                      {formatNumber(
                                        slab.delivery_charges,
                                      )}
                                    </span>

                                    <span className="text-gray-300">
                                      •
                                    </span>

                                    <span className="font-medium text-blue-600">
                                      ₹
                                      {formatNumber(
                                        slab.per_km_charges,
                                      )}
                                      /KM
                                    </span>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </td>

                        {/* Count */}
                        <td className="px-3 py-3 text-center">
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-[10px] font-medium text-purple-600">
                            <Layers3 size={11} />
                            {slabs.length}
                          </span>
                        </td>

                        {/* Manage */}
                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            onClick={() =>
                              onManage?.(group)
                            }
                            className="inline-flex items-center gap-1.5 rounded-lg bg-orange-50 px-2.5 py-1.5 text-[11px] font-medium text-orange-600 transition hover:bg-orange-100"
                          >
                            <Settings2 size={12} />
                            Manage
                          </button>
                        </td>
                      </tr>
                    );
                  },
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DeliveryChargesTab;